import { normalizeAccumulatorOrder } from '../../libs/accumulatorEconomics';
import { useState, useEffect, useContext, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Layout from '../../layouts/user';
import UserHead from '../../components/user/head';
import { InfoAlert, WarnAlert } from '../../components/user/alert';
import Card from '../../components/invest/card';
import InvestedPlans from '../../components/invest/investedPlans';
import EconomicsView from '../../components/economics/EconomicsView';
import { post, download, get, upload } from '../../libs/api';
import { isOkResponse, responseData } from '../../libs/apiResponse';
import {
  showPrice,
  showPercent,
  showDateTime,
  showAmount,
  payoutDateTime,
  showDate,
} from '../../libs/util';
import { groupInteger } from '../../libs/numberFormat';
import { Errors, Successes } from '../../components/common/msg';
import UserContext from '../../libs/context';
import { NumericFormat } from 'react-number-format';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';

const ENGLISH_SHORT_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const toLocalCalendarDate = (value) => {
  if (value === null || value === undefined || value === '') return null;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  if (typeof value === 'number') {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  const text = String(value).trim();
  if (!text) return null;

  const localDateMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (localDateMatch) {
    const [, yearText, monthText, dayText] = localDateMatch;
    const year = Number(yearText);
    const month = Number(monthText);
    const day = Number(dayText);
    const date = new Date(year, month - 1, day);

    if (
      date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day
    ) {
      return date;
    }
    return null;
  }

  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};

const addLocalMonths = (date, monthOffset) => {
  const next = new Date(date.getTime());
  const day = next.getDate();

  next.setDate(1);
  next.setMonth(next.getMonth() + monthOffset);

  const lastDay = new Date(
    next.getFullYear(),
    next.getMonth() + 1,
    0
  ).getDate();
  next.setDate(Math.min(day, lastDay));

  return next;
};

const formatBarrierStartDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const day = String(date.getDate()).padStart(2, '0');
  return `${day} ${ENGLISH_SHORT_MONTHS[date.getMonth()]} ${date.getFullYear()}`;
};

export const calculateAccumBarrierStartDate = (
  valueDatetime,
  noOfKoPeriods,
  monthlyOrWeekly
) => {
  const valueDate = toLocalCalendarDate(valueDatetime);
  if (!valueDate) return null;

  const koPeriods = Number(noOfKoPeriods ?? 0);
  if (!Number.isFinite(koPeriods) || koPeriods <= 0) {
    return formatBarrierStartDate(valueDate);
  }

  const cadence =
    monthlyOrWeekly === null || monthlyOrWeekly === undefined
      ? ''
      : String(monthlyOrWeekly).toLowerCase();
  let barrierStartDate = new Date(valueDate.getTime());

  if (cadence === 'weekly') {
    barrierStartDate.setDate(barrierStartDate.getDate() + koPeriods * 7);
    barrierStartDate.setDate(barrierStartDate.getDate() + 1);
  } else if (cadence === 'monthly') {
    barrierStartDate = addLocalMonths(barrierStartDate, koPeriods);
    barrierStartDate.setDate(barrierStartDate.getDate() + 1);
  }

  return formatBarrierStartDate(barrierStartDate);
};

function Page({ isNew }) {
  const { orderId } = useParams();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [successes, setSuccesses] = useState([]);
  const [order, setOrder] = useState();
  const isAccumulator = ['ACCUM', 'ACCUM_REV'].includes(order?.product?.product_type);
  const { notification } = useContext(UserContext);
  const [isRolled, setIsRolled] = useState(false);
  const [rolloverAmt, setrolloverAmt] = useState(0);
  const [isEditMode, setIsEditMode] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [showRateChangeModal, setShowRateChangeModal] = useState(false);
  const [currentCommsRate, setCurrentCommsRate] = useState(0);
  const [newCommissionRate, setNewCommissionRate] = useState('');
  const [attachment, setAttachment] = useState(null);
  const AGENT_REQUEST_CATEGORY = 12;
  const [payoutData, setPayoutData] = useState([]);
  const ITEMS_PER_PAGE = 5;
  const [currentPage, setCurrentPage] = useState(1);
  const [showRedeem, setShowRedeem] = useState(false);
  const [enableRedeem, setEnableRedeem] = useState(false);
  const [accumDatas, setAccumData] = useState();
  const ACCUM_ITEMS_PER_PAGE = 10;
  const [accumCurrentPage, setAccumCurrentPage] = useState(1);
  const [accumFixingRows, setAccumFixingRows] = useState([]);
  const [accumFixingLoading, setAccumFixingLoading] = useState(false);
  const [accumFixingError, setAccumFixingError] = useState('');
  const [accumFixingPage, setAccumFixingPage] = useState(1);
  const [enableOneDay, setEnableOneDay] = useState(false);

  const loadOrder = async () => {
    setLoading(true);
    const data = await post('/investment/order/get', { orderId });
    setLoading(false);
    if (data?.status != 200) {
      setErrors([data?.error]);
      return;
    }

    setOrder(normalizeAccumulatorOrder(data.data));
    if (data.data) {
      setrolloverAmt(data.data.term_expired_amount);
      setCurrentCommsRate(data.data.commsRate);
      setIsRolled(true);
    }
    if (
      data.data.product?.product_type === 'CASH_CARRY' &&
      data.data.product?.description?.includes('USD')
    ) {
      setEnableOneDay(true);
    } else {
      setEnableOneDay(false);
    }
    // if (!data.data.is_flexi_redeemed) {
    //   setEnableRedeem(true);
    // }
    // if (
    //   data.data.product.product_type === 'PSFX' &&
    //   data.data.auto_enroll_psflexi === 1
    // ) {
    //   setShowRedeem(true);
    // }

    if (isNew) {
      setSuccesses([
        'Your order to invest in this product has been submitted.',
      ]);
    }

    // Set canEdit inside loadOrder after the order has been fetched.
    const currentTime = new Date(); // Current time in UTC

    // Add 'Z' to interpret the time as UTC
    const maturityTime = new Date(data.data.maturity_datetime + 'Z');

    const timeDifferenceInMs = maturityTime.getTime() - currentTime.getTime();
    const differenceInHours = timeDifferenceInMs / 36e5; // Convert from milliseconds to hours.

    console.log('>>!> currentTime:', currentTime);
    console.log('<<!< maturityTime:', maturityTime);

    setCanEdit(differenceInHours >= 24);

    // PB-1539: /payout/transactions is served by backend-api, which returns the
    // { code: 0, data } envelope (no `status` field). Gate on isOkResponse so the
    // Payout Schedule actually renders, and don't early-return on a payout miss
    // (that previously also skipped the accumulator fetch below).
    const payoutResp = await get(`/payout/transactions/${orderId}`);
    if (isOkResponse(payoutResp)) {
      setPayoutData(responseData(payoutResp, []));
    }
    const accumData = await get(`/investment/accum_order/${orderId}`);
    setAccumData(accumData.data);
  };

  const handleAgentRateSubmit = async (e) => {
    e.preventDefault();
    setErrors([]);
    setSuccesses([]);
    setLoading(true);
    if (!attachment) {
      setErrors(['Please upload a file before submitting.']);
      setLoading(false);
      return;
    }

    let fileId1;
    try {
      const uploadData = await upload(
        '/wallet/file/upload',
        attachment,
        true,
        AGENT_REQUEST_CATEGORY
      );
      if (uploadData?.status !== 200) {
        throw new Error('Error uploading file');
      }
      fileId1 = uploadData.data.file_id;
      console.log('attachment uploaded');

      const params = {
        comms_rate: newCommissionRate,
        transaction_id: order.transaction_id,
        client_id: order.client_id,
        att1_file_id: fileId1,
        current_comms_rate: currentCommsRate,
      };

      const data = await post('/wallet/request/agent_rate', params);
      setLoading(false);
      if (data?.status !== 200) {
        setErrors([data.error]);
      } else {
        setSuccesses(['Agent rate change submitted successfully.']);
      }
    } catch (error) {
      setErrors([error.message]);
      setLoading(false);
    }
    setShowRateChangeModal(false);
  };

  useEffect(() => {
    loadOrder();
  }, []);

  useEffect(() => {
    if (notification?.type == 2) {
      // order approved
      console.log('!!!  order approved', notification?.type);
      refreshOrder();
    }
  }, [notification]);

  const refreshOrder = async () => {
    await loadOrder();
    setSuccesses([]);
  };

  const handleRefresh = async (e) => {
    e.preventDefault();
    refreshOrder();
  };

  const handleDownload = async (e) => {
    e.preventDefault();

    const blob = await download('/investment/order/pdf', { orderId });
    // Create blob link to download
    const url = window.URL.createObjectURL(new Blob([blob]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', order?.file_name);

    // Append to html link element page
    document.body.appendChild(link);

    // Start download
    link.click();

    // Clean up and remove the link
    link.parentNode.removeChild(link);
  };

  const handleEdit = () => {
    setIsEditMode(true);
  };

  const handleCancel = () => {
    setIsEditMode(false);
  };

  function handleAmountChange(values) {
    const { value } = values;
    setrolloverAmt(value);
  }

  const handleSave = async () => {
    setLoading(true);
    console.log('>>>> rolloverAmt is ' + rolloverAmt);
    if (isRolled) {
      if (
        rolloverAmt < order.product.min_amount ||
        rolloverAmt > order.term_expired_amount
      ) {
        let errorMessage = `Amount must be between ${groupInteger(
          order.product.min_amount
        )} and ${groupInteger(order.term_expired_amount)}`;
        console.log('>>> ' + errorMessage);

        setErrors([errorMessage]);
        // setLoading(false);
        return;
      }
    }

    const data = await post('/investment/cc_order/edit', {
      transaction_id: orderId,
      rollover_amt: rolloverAmt,
    });
    setLoading(false);
    if (data?.status != 200) {
      setErrors([data?.error]);
      return;
    }
    setSuccesses(['Rollover has been updated successfully.']);
    setIsEditMode(false);
    loadOrder();
  };

  const handleIsRolledChange = () => {
    if (isRolled) {
      setrolloverAmt(0);
    }
    setIsRolled(!isRolled);
  };

  const isPut = order?.product?.option?.option_type == 'PUT';

  const formatNumber = (numString) => {
    // strip out all non-digit characters
    const stripped = numString.replace(/\D/g, '');
    // perform the formatting
    return stripped.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
  };

  const formatApiDateTime = (value) => {
    if (!value) return null;
    if (value instanceof Date) {
      return value
        .toISOString()
        .replace('T', ' ')
        .replace('Z', '')
        .slice(0, 19);
    }
    if (typeof value === 'number') {
      return new Date(value)
        .toISOString()
        .replace('T', ' ')
        .replace('Z', '')
        .slice(0, 19);
    }
    return String(value).replace('T', ' ').replace('Z', '').slice(0, 19);
  };

  const hasValue = (value) =>
    value !== undefined && value !== null && value !== '';

  const getValue = (obj, keys, fallback) => {
    for (const key of keys) {
      const value = obj?.[key];
      if (hasValue(value)) {
        return value;
      }
    }
    return fallback;
  };

  const firstValue = (...values) => {
    const value = values.find(hasValue);
    return value === undefined ? '' : value;
  };

  const extractFixingDateRows = (response) => {
    const candidates = [
      response?.data?.fixing_settlement_dates,
      response?.data?.data,
      response?.data?.list,
      response?.data?.rows,
      response?.data,
      response?.result?.data,
      response?.result?.list,
      response?.result,
    ];

    for (const item of candidates) {
      if (Array.isArray(item)) {
        return item;
      }
    }

    return [];
  };

  const extractFixingHistoryRows = (response) => {
    const candidates = [
      response?.data?.fixing_history,
      response?.data?.history,
      response?.data?.fixingHistory,
      response?.data?.data?.fixing_history,
      response?.data?.data?.history,
      response?.result?.fixing_history,
      response?.result?.history,
      response?.result?.data?.fixing_history,
      response?.result?.data?.history,
    ];

    for (const item of candidates) {
      if (Array.isArray(item)) {
        return item;
      }
    }

    return [];
  };

  const normalizeDateKey = (value) => {
    if (!value) return '';
    return String(value).replace('T', ' ').replace('Z', '').trim().slice(0, 19);
  };

  const floorToDecimals = (value, decimals) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return 0;
    const factor = 10 ** decimals;
    return Math.floor(num * factor) / factor;
  };

  const parseNumeric = (value, fallback = 0) => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === 'number')
      return Number.isFinite(value) ? value : fallback;
    const normalized = String(value).replace(/,/g, '').trim();
    if (!normalized) return fallback;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const handlerolloverAmtChange = (e) => {
    const formattedValue = formatNumber(e.target.value);
    setrolloverAmt(formattedValue);
  };

  const getAccumNotionalCcy = () => {
    if (!order || !isAccumulator) return '';
    const spotCcy = order.custom_ebtca_product?.underlying || '';
    const quoteCcy = spotCcy.includes('/') ? spotCcy.split('/')[1] : '';
    return order.notional_amount_ccy || order.invest_ccy || quoteCcy || '';
  };

  const getTitle = () => {
    let title = order?.product?.description || '';
    if (title.includes('DCI')) {
      if (title.includes('USD-P')) {
        title = title.replace(/USD-[CP]/, 'USDC - BUY LOW');
      } else if (title.includes('USD-C')) {
        title = title.replace(/USD-[CP]/, 'USDC - SELL HIGH');
      } else {
        title = title.replace(/USD-[CP]/, 'USDC');
      }
    } else if (isAccumulator) {
      // For ACCUM orders, derive title from actual order parameters
      const rawFrequency = String(
        order?.custom_ebtca_product?.frequency || ''
      ).trim();
      const periodLabel = rawFrequency.toUpperCase().includes('MONTH')
        ? 'Months'
        : rawFrequency.toUpperCase().includes('WEEK')
          ? 'Weeks'
          : rawFrequency;
      const noFixings =
        order?.custom_ebtca_product?.no_fixings || order?.tenor || '';
      const ccy = getAccumNotionalCcy();
      title = `Accumulator ${noFixings} ${periodLabel} ${ccy}`;
    } else {
      title = order?.product?.display_title || '';
    }
    if (order?.option_list && !title.includes('DCI')) {
      const quoteCurrency = order.option_list[0].quoteCurrency;
      title = `${order?.product?.description} - (with ${quoteCurrency} Option)`;
    }
    // Fix common typo: USTD -> USDT
    title = title.replace(/USTD/g, 'USDT');
    return title;
  };

  const totalPages = Math.ceil(payoutData.length / ITEMS_PER_PAGE);

  const paginatedData = payoutData.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handleRedeemed = async () => {
    const data = await post('/investment/psfx/redeem', order);
    if (data?.status !== 200) {
      setErrors([data.error]);
    } else {
      setSuccesses(['Redeem submitted successfully.']);
      loadOrder();
    }
  };

  const totalAccumPages = accumDatas
    ? Math.ceil(accumDatas.length / ACCUM_ITEMS_PER_PAGE)
    : 0;

  const paginatedAccumData = accumDatas
    ? accumDatas.slice(
        (accumCurrentPage - 1) * ACCUM_ITEMS_PER_PAGE,
        accumCurrentPage * ACCUM_ITEMS_PER_PAGE
      )
    : [];

  useEffect(() => {
    let cancelled = false;

    const loadAccumFixingDates = async () => {
      if (!isAccumulator) {
        setAccumFixingRows([]);
        setAccumFixingError('');
        setAccumFixingLoading(false);
        return;
      }

      const payload = {
        value_datetime: formatApiDateTime(order?.value_datetime),
        maturity_datetime: formatApiDateTime(order?.maturity_datetime),
        fixing_frequency: String(
          order?.custom_ebtca_product?.frequency || ''
        ).trim(),
        transaction_id: orderId,
        deal_datetime: formatApiDateTime(order?.value_datetime),
        period_count: order.custom_ebtca_product?.no_fixings,
      };

      if (
        !payload.value_datetime ||
        !payload.maturity_datetime ||
        !payload.fixing_frequency
      ) {
        setAccumFixingRows([]);
        setAccumFixingError('');
        setAccumFixingLoading(false);
        return;
      }

      setAccumFixingLoading(true);
      setAccumFixingError('');

      try {
        const response = await post(
          '/investment/fixing_dates/calculate',
          payload
        );
        if (cancelled) {
          return;
        }

        if (response?.status !== 200) {
          setAccumFixingRows([]);
          setAccumFixingError(
            response?.error || 'Failed to load fixing and settlement dates.'
          );
          return;
        }

        const historyRows = extractFixingHistoryRows(response);
        const historyByFixingDate = new Map();
        const historyBySettlementDate = new Map();
        const historyByFixingAndSettlement = new Map();

        historyRows.forEach((history) => {
          const historyFixingDate = normalizeDateKey(
            getValue(
              history,
              [
                'fixing',
                'fixing_date',
                'fixingDate',
                'fixing_datetime',
                'value_datetime',
              ],
              ''
            )
          );
          const historySettlementDate = normalizeDateKey(
            getValue(
              history,
              [
                'settlement',
                'settlement_date',
                'settlementDate',
                'settlement_datetime',
                'maturity_datetime',
              ],
              ''
            )
          );
          const historyData = {
            actualAmount: getValue(
              history,
              [
                'actual_amount',
                'actualAmount',
                'investment_amount',
                'investmentAmount',
                'invest_amount',
                'investAmount',
                'amount',
                'notional_amount',
                'notionalAmount',
                'notionalSpent',
              ],
              ''
            ),
            refundAmount: getValue(
              history,
              ['refund_amount', 'refundAmount'],
              ''
            ),
            btcPurchased: getValue(
              history,
              ['btc_purchased', 'btcPurchased'],
              ''
            ),
            ethPurchased: getValue(
              history,
              ['eth_purchased', 'ethPurchased'],
              ''
            ),
            btcSpotPrice: getValue(
              history,
              ['btc_spot_price', 'btcSpotPrice'],
              ''
            ),
            ethSpotPrice: getValue(
              history,
              ['eth_spot_price', 'ethSpotPrice'],
              ''
            ),
            tradeStatus: getValue(history, ['trade_status', 'tradeStatus'], ''),
            koPrice: getValue(history, ['ko_price', 'koPrice'], ''),
          };

          if (historyFixingDate) {
            historyByFixingDate.set(historyFixingDate, historyData);
          }
          if (historySettlementDate) {
            historyBySettlementDate.set(historySettlementDate, historyData);
          }
          if (historyFixingDate && historySettlementDate) {
            historyByFixingAndSettlement.set(
              `${historyFixingDate}|${historySettlementDate}`,
              historyData
            );
          }
        });

        const rows = extractFixingDateRows(response).map((row, index) => {
          const fixingDate = getValue(
            row,
            [
              'fixing',
              'fixing_date',
              'fixingDate',
              'fixing_datetime',
              'value_datetime',
            ],
            ''
          );
          const settlementDate = getValue(
            row,
            [
              'settlement',
              'settlement_date',
              'settlementDate',
              'settlement_datetime',
              'maturity_datetime',
            ],
            ''
          );
          const fixingKey = normalizeDateKey(fixingDate);
          const settlementKey = normalizeDateKey(settlementDate);
          const matchedHistory =
            historyByFixingAndSettlement.get(`${fixingKey}|${settlementKey}`) ||
            historyByFixingDate.get(fixingKey) ||
            historyBySettlementDate.get(settlementKey) ||
            {};
          const actualAmount = firstValue(
            getValue(
              row,
              [
                'actual_amount',
                'actualAmount',
                'investment_amount',
                'investmentAmount',
                'invest_amount',
                'investAmount',
                'amount',
                'notional_amount',
                'notionalAmount',
                'notionalSpent',
              ],
              ''
            ),
            matchedHistory.actualAmount
          );
          const refundAmount = firstValue(
            getValue(row, ['refund_amount', 'refundAmount'], ''),
            matchedHistory.refundAmount
          );
          const btcPurchased = firstValue(
            getValue(row, ['btc_purchased', 'btcPurchased'], ''),
            matchedHistory.btcPurchased
          );
          const ethPurchased = firstValue(
            getValue(row, ['eth_purchased', 'ethPurchased'], ''),
            matchedHistory.ethPurchased
          );
          const btcSpotPrice = firstValue(
            getValue(row, ['btc_spot_price', 'btcSpotPrice'], ''),
            matchedHistory.btcSpotPrice
          );
          const ethSpotPrice = firstValue(
            getValue(row, ['eth_spot_price', 'ethSpotPrice'], ''),
            matchedHistory.ethSpotPrice
          );
          const tradeStatus = firstValue(
            getValue(row, ['trade_status', 'tradeStatus'], ''),
            matchedHistory.tradeStatus
          );
          const koPrice = firstValue(
            getValue(row, ['ko_price', 'koPrice'], ''),
            matchedHistory.koPrice
          );

          return {
            id: index + 1,
            fixingDate,
            settlementDate,
            actualAmount,
            refundAmount,
            btcPurchased,
            ethPurchased,
            btcSpotPrice,
            ethSpotPrice,
            tradeStatus,
            koPrice,
          };
        });

        setAccumFixingRows(rows);
      } catch (_) {
        if (!cancelled) {
          setAccumFixingRows([]);
          setAccumFixingError('Failed to load fixing and settlement dates.');
        }
      } finally {
        if (!cancelled) {
          setAccumFixingLoading(false);
        }
      }
    };

    loadAccumFixingDates();

    return () => {
      cancelled = true;
    };
  }, [order]);

  useEffect(() => {
    setAccumFixingPage(1);
  }, [accumFixingRows]);

  const totalAccumFixingPages = Math.max(
    1,
    Math.ceil(accumFixingRows.length / ACCUM_ITEMS_PER_PAGE)
  );

  const paginatedAccumFixingRows = accumFixingRows.slice(
    (accumFixingPage - 1) * ACCUM_ITEMS_PER_PAGE,
    accumFixingPage * ACCUM_ITEMS_PER_PAGE
  );

  // Native funding currency takes precedence over the reference pair for both ACCUM families.
  const accumNotionalCcy = getAccumNotionalCcy();
  const noFixings =
    parseNumeric(order?.custom_ebtca_product?.no_fixings, 0) ||
    accumFixingRows.length;
  const multiplier = parseNumeric(order?.custom_ebtca_product?.boosted_multiplier, 0);
  const orderNotionalAmt = parseNumeric(order?.notional_amount, 0);
  const investAmt = parseNumeric(
    order?.invest_amount ?? order?.investment_amount,
    0
  );
  const referenceRate = parseNumeric(order?.reference_rate, 0);
  // PB-1046: DCI books against product_type 'OPTIONS', which ACCUM and PS also use, so the
  // product record cannot identify it. txn_form_type is the reliable marker.
  const isDci = `${order?.txn_form_type ?? ''}`.toUpperCase() === 'DCI';
  const derivedNotionalAmt = parseFloat((investAmt * referenceRate).toFixed(2));
  const notionalAmt =
    Number.isFinite(orderNotionalAmt) && orderNotionalAmt > 0
      ? orderNotionalAmt
      : derivedNotionalAmt;
  const investmentPerFixing = noFixings > 0 ? notionalAmt / noFixings : 0;
  const accumPurchaseAmount1 =
    multiplier > 0 ? floorToDecimals(investmentPerFixing / multiplier, 2) : 0;
  const accumPurchaseAmount2 = floorToDecimals(investmentPerFixing, 2);
  const accumBarrierStartDate =
    isAccumulator
      ? calculateAccumBarrierStartDate(
          order?.value_datetime,
          order?.custom_ebtca_product?.guarantee_periods,
          order?.custom_ebtca_product?.frequency
        )
      : null;
  const formatAccumAmount = (value) =>
    new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value || 0);
  const formatActualAmount = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const parsed = parseNumeric(value, NaN);
    return Number.isFinite(parsed) ? formatAccumAmount(parsed) : String(value);
  };
  const formatScheduleValue = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const parsed = parseNumeric(value, NaN);
    return Number.isFinite(parsed) ? formatAccumAmount(parsed) : String(value);
  };
  // PB-1990: groups the integer part for readability while keeping every
  // decimal digit. useGrouping stays false so toLocaleString only flattens
  // exponent notation — groupInteger is what inserts the separators, and it
  // never touches anything right of the decimal point.
  const formatFullPrecisionValue = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    if (typeof value === 'number') {
      if (!Number.isFinite(value)) return '-';
      const displayValue = String(value);
      if (!displayValue.includes('e') && !displayValue.includes('E')) {
        return groupInteger(displayValue);
      }
      return groupInteger(
        value.toLocaleString('en-US', {
          useGrouping: false,
          maximumSignificantDigits: 21,
        })
      );
    }
    const displayValue = String(value).trim();
    return displayValue ? groupInteger(displayValue) : '-';
  };
  const accumSpotReferenceCcy = String(
    order?.custom_ebtca_product?.underlying ||
    order?.custom_ebtca_product?.spot_refernce_ccy ||
    ''
  ).toUpperCase();
  const showBtcColumns = accumSpotReferenceCcy.includes('BTC');
  const showEthColumns = accumSpotReferenceCcy.includes('ETH');
  const accumScheduleColumnCount =
    8 + (showBtcColumns ? 2 : 0) + (showEthColumns ? 2 : 0);

  return (
    <Layout menu="investment">
      <UserHead
        sub="Investment"
        title={getTitle()}
        showBack={true}
        backTo="/portal/investment"
      >
        {order && (
          <ul className="nk-block-tools gx-3">
            <li>
              <a
                href="#"
                className="btn btn-light btn-white"
                onClick={handleRefresh}
              >
                <span>Refresh</span>
              </a>
            </li>
            {order.file_id && (
              <li>
                <a
                  href="#"
                  className="btn btn-primary"
                  onClick={handleDownload}
                >
                  <span>Download</span>
                </a>
              </li>
            )}
            {/* {!isEditMode && canEdit && (
              <li>
                <a href="#" className="btn btn-primary" onClick={handleEdit}>
                  <span>Rollover</span>
                </a>
              </li>
            )} */}
            {currentCommsRate && order?.order_status !== 'REJECTED' ? (
              <li>
                <a
                  href="#"
                  className="btn btn-primary"
                  onClick={() => setShowRateChangeModal(true)}
                >
                  <span>Agent Rate Change</span>
                </a>
              </li>
            ) : (
              ''
            )}
            {/* {showRedeem && (
              <li>
                <button
                  className="btn btn-primary"
                  onClick={() => handleRedeemed()}
                  disabled={enableRedeem}
                >
                  <span>Redeem</span>
                </button>
              </li>
            )} */}
          </ul>
        )}
      </UserHead>

      <Successes msgs={successes} />

      {/* Must stay outside the `order &&` block below — a failed loadOrder
          leaves order null, which previously swallowed the error and rendered
          a blank page. */}
      {errors?.length > 0 && <Errors errors={errors} />}

      <div className="mt-1 mb-4 d-flex align-items-center flex-wrap">
        <span className="text-muted mr-2">Reference:</span>
        <span
          className="mr-4 px-2 py-1"
          style={{
            fontFamily: 'monospace',
            fontWeight: 600,
            background: '#f5f6fa',
            borderRadius: 4,
            letterSpacing: '0.5px',
          }}
        >
          {order?.reference_number}
        </span>
        <span className="text-muted mr-2">Transaction ID:</span>
        <span
          className="mr-4 px-2 py-1"
          style={{
            fontFamily: 'monospace',
            fontWeight: 600,
            background: '#f5f6fa',
            borderRadius: 4,
          }}
        >
          {order?.transaction_id || orderId}
        </span>
        <span className="text-muted mr-2">Status:</span>
        <span
          className={`badge badge-${
            {
              PENDING: 'danger',
              'PEND APPROVAL': 'warning',
              'PEND CONFIRM': 'warning',
              ACTIVE: 'success',
              VERIFIED: 'success',
              COMPLETED: 'success',
              CANCELLED: 'secondary',
              REJECTED: 'danger',
            }[order?.order_status] || 'secondary'
          }`}
          style={{ fontSize: '0.85rem', padding: '0.35em 0.65em' }}
        >
          {order?.order_status}
        </span>
      </div>

      {order &&
        (order.structure_version_id != null ? (
          <EconomicsView json={order.economics} />
        ) : (
          <>
          <div className="nk-odr-list is-stretch card card-bordered ">
            <div className="row p-2 border-bottom">
              <div className="col-12 nk-odr-label">
                {/* PB-1839 made invest_amount the notional. For DCI the notional has
                    its own labelled row below, so the headline shows what the client paid. */}
                {isDci && order.contributionAmount != null
                  ? showPrice(
                      order.contributionAmount,
                      order.contributionAmountCcy,
                      null,
                      true
                    )
                  : enableOneDay
                  ? showPrice(
                      order.invest_amount,
                      order.invest_ccy,
                      null,
                      true
                    ) +
                    ' (' +
                    showPrice(
                      order.contributionAmount,
                      order.contributionAmountCcy,
                      null,
                      true
                    ) +
                    ')'
                  : showAmount(order.invest_amount, null, 8, false) +
                    ' ' +
                    order.invest_ccy}
              </div>
              <div
                className={`${enableOneDay ? 'col-8' : 'col-12'} nk-odr-meta`}
              >
                Invested
              </div>
              {enableOneDay && (
                <div className="col-4 ">
                  <span className="float-left">Trade Date: </span>
                  <span className="float-right">
                    {showDateTime(order.create_time)
                      ? showDateTime(order.create_time)
                      : '-'}
                  </span>
                </div>
              )}
            </div>

            {order.product?.option?.strike_price && (
              <div className="row p-2 border-bottom">
                <div className="col-4">&nbsp;</div>
                <div className="col-4">
                  <span className="float-left">Strike Rate: </span>
                  <span className="float-right">
                    {/* Strike quotes use the USD reference; settlement currencies are separate. */}
                    {showPrice(
                      order.product?.option?.strike_price,
                      'USD'
                    )}
                  </span>
                </div>
                <div className="col-4">&nbsp;</div>
              </div>
            )}

            {order.product.product_type !== 'SSPS' &&
              !isAccumulator && (
                <div className="row p-2 border-bottom">
                  <div className="col-4">
                    <span className="float-left">Tenure: </span>
                    <span className="float-right">{order.tenor} days</span>
                  </div>
                  {enableOneDay ? (
                    <div className="col-4">
                      <span className="float-left">Reference Rate:</span>
                      <span className="float-right">
                        {order.reference_rate}
                      </span>
                    </div>
                  ) : (
                    <div className="col-4">
                      <span className="float-left">
                        {order.option_list
                          ? 'Desired Fixed Rate:'
                          : 'Funding Rate:'}
                      </span>
                      <span className="float-right">
                        {showPercent(order.term_apy)}
                      </span>
                    </div>
                  )}
                  {/* <div className="col-4">
                <span className="float-left">Maturity Date: </span>
                <span className="float-right">
                  {order.redemption_datetime
                    ? showDateTime(order.redemption_datetime)
                    : "TBA"}
                </span>
              </div> */}
                  {enableOneDay ? (
                    <div className="col-4">
                      <span className="float-left">Effective Date: </span>
                      <span className="float-right">
                        {showDateTime(order.value_datetime)
                          ? showDateTime(order.value_datetime)
                          : '-'}
                      </span>
                    </div>
                  ) : (
                    <div className="col-4">
                      <span className="float-left">Maturity Date: </span>
                      <span className="float-right">
                        {showDateTime(order.maturity_datetime)
                          ? showDateTime(order.maturity_datetime)
                          : '-'}
                      </span>
                    </div>
                  )}
                </div>
              )}

            {/* PB-1046: DCI carries a reference rate that converts the client
                contribution into the notional, the same way PS/PSL do. */}
            {isDci && (
              <div className="row p-2 border-bottom">
                <div className="col-4">
                  <span className="float-left">Reference Rate: </span>
                  <span className="float-right">
                    {order.reference_rate != null ? order.reference_rate : '-'}
                  </span>
                </div>
                <div className="col-4">
                  <span className="float-left">Client Contribution: </span>
                  <span className="float-right">
                    {order.contributionAmount != null
                      ? showPrice(
                          order.contributionAmount,
                          order.contributionAmountCcy,
                          null,
                          true
                        )
                      : '-'}
                  </span>
                </div>
                <div className="col-4">
                  <span className="float-left">Notional Amount: </span>
                  <span className="float-right">
                    {order.notional_amount != null
                      ? showPrice(
                          order.notional_amount,
                          order.notional_amount_ccy,
                          null,
                          true
                        )
                      : '-'}
                  </span>
                </div>
              </div>
            )}

            {order.product.product_type !== 'SSPS' &&
              !isAccumulator && (
                <div className="row p-2 border-bottom">
                  {
                    order.option_list && (
                      <div className="col-4">
                        <span className="float-left">Participation Rate: </span>
                        <span className="float-right">
                          {parseFloat(order.participation_rate) * 100}%
                        </span>
                      </div>
                    )
                    // : (
                    //   //  order.product.product_type == 'CASH_CARRY' ? (
                    //   //   <div className="col-4">
                    //   //     <span className="float-left">PS Flexi opt-in : </span>
                    //   //     <span className="float-right">
                    //   //       {order.auto_enroll_psflexi === 1 ? 'True' : 'False'}
                    //   //     </span>
                    //   //   </div>
                    //   // ) :
                    //   <div className="col-4"></div>
                    // )
                  }
                  {!enableOneDay && <div className="col-4"></div>}
                  <div className="col-4 ">
                    {order.product.product_type == 'CASH_CARRY' ||
                    order.product.product_type == 'PSL' ||
                    order.product.product_type === 'PSFX' ||
                    order.product.product_type == 'PPL' ||
                    order.product.product_type == 'PSLFX' ? (
                      <span className="float-left">
                        {order.contributionAmountCcy === 'USD'
                          ? 'Final Notional Exchange:'
                          : 'Return'}
                      </span>
                    ) : (
                      <span className="float-left">
                        Final Notional Exchange (Final Index Level{' '}
                        {isPut
                          ? String.fromCharCode(8805)
                          : String.fromCharCode(8804)}{' '}
                        Strike):{' '}
                      </span>
                    )}
                    <span className="float-right">
                      {showPrice(
                        order.term_expired_amount,
                        order.term_expired_amount_ccy,
                        null,
                        true
                      )}
                    </span>
                  </div>
                  {/* <div className="col-4 ">
                <span className="float-left">Reference Rate: </span>
                <span className="float-right">
                  {order.payment_reference ? order.payment_reference : "TBA"}
                </span>
              </div> */}
                  {enableOneDay && (
                    <div className="col-4">
                      <span className="float-left">Funding Rate:</span>
                      <span className="float-right">
                        {' '}
                        {order.term_apy != null
                          ? `${(order.term_apy * 100).toFixed(2)}%`
                          : '-'}
                      </span>
                    </div>
                  )}
                  {enableOneDay && (
                    <div className="col-4">
                      <span className="float-left">Maturity Date: </span>
                      <span className="float-right">
                        {showDateTime(order.maturity_datetime)
                          ? showDateTime(order.maturity_datetime)
                          : '-'}
                      </span>
                    </div>
                  )}
                </div>
              )}

            {order.product.product_type === 'SSPS' && (
              <>
                <div className="row p-2 border-bottom">
                  <div className="col-4">
                    <span className="float-left">Tenure: </span>
                    <span className="float-right">
                      {order.accumProductDetails.calculationPeriod} days
                    </span>
                  </div>
                  {/* <div className="col-4">
                <span className="float-left">Maturity Date: </span>
                <span className="float-right">
                  {order.redemption_datetime
                    ? showDateTime(order.redemption_datetime)
                    : "TBA"}
                </span>
              </div> */}
                  <div className="col-4">
                    <span className="float-left">Maturity Date: </span>
                    <span className="float-right">
                      {showDateTime(order.maturity_datetime)
                        ? showDateTime(order.maturity_datetime)
                        : '-'}
                    </span>
                  </div>
                  <div className="col-4 ">
                    <span className="float-left">Trade Date: </span>
                    <span className="float-right">
                      {showDateTime(order.value_datetime)
                        ? showDateTime(order.value_datetime)
                        : '-'}
                    </span>
                  </div>
                </div>
                <div className="row p-2 border-bottom">
                  <div className="col-4">
                    <span className="float-left">Price Discount Factor: </span>
                    <span className="float-right">
                      {order.accumProductDetails.discountRate} {'%'}
                    </span>
                  </div>
                  <div className="col-4">
                    <span className="float-left">
                      Normal-day participation:{' '}
                    </span>
                    <span className="float-right">
                      {'1x'}
                    </span>
                  </div>
                  <div className="col-4">
                    <span className="float-left">
                      Participation Scaling Factor:{' '}
                    </span>
                    <span className="float-right">
                      {order.accumProductDetails.multiplier ?? order.accumProductDetails.multiplier2x} {'x'}
                    </span>
                  </div>
                </div>
                <div className="row p-2 border-bottom">
                  <div className="col-4">
                    <span className="float-left">
                      Underlying Performance Trigger:{' '}
                    </span>
                    <span className="float-right">
                      {order.accumProductDetails.downsideTriggerLevel} {'%'}
                    </span>
                  </div>
                </div>
              </>
            )}
            {isAccumulator && (
              <>
                <div className="row p-2 border-bottom">
                  <div className="col-4">
                    <div className="d-flex justify-content-between">
                      <span>Reference Rate :</span>
                      <span>{`${order?.reference_rate}`}</span>
                    </div>
                  </div>
                  <div className="col-4"></div>
                  <div className="col-4">
                    <div className="d-flex justify-content-between">
                      <span>Notional Amount :</span>
                      <span>
                        {order?.notional_amount != null
                          ? `${groupInteger(
                              order.notional_amount
                            )} ${accumNotionalCcy}`
                          : '-'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="row p-2 border-bottom">
                  <div className="col-4">
                    <div className="d-flex justify-content-between">
                      <span>Spot Reference :</span>
                      <span>
                        {groupInteger(
                          order.custom_ebtca_product?.spot_reference
                        ) || '-'}{' '}
                        {`${order.custom_ebtca_product?.underlying}`}
                      </span>
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="d-flex justify-content-between">
                      <span>Number of Periods :</span>
                      <span>
                        {order.custom_ebtca_product?.no_fixings ?? '-'}
                      </span>
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="d-flex justify-content-between">
                      <span>Fixing Frequency :</span>
                      <span>
                        {order.custom_ebtca_product?.frequency ?? '-'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="row p-2 border-bottom">
                  <div className="col-4">
                    <div className="d-flex justify-content-between">
                      <span>Maturity Date :</span>
                      <span>
                        {order.maturity_datetime
                          ? showDate(order.maturity_datetime) + ' 08:00 UTC'
                          : '-'}
                      </span>
                    </div>
                  </div>
                  <div className="col-4"></div>
                  <div className="col-4"></div>
                </div>
                <div className="row p-2 border-bottom">
                  <div className="col-4">
                    <div className="d-flex justify-content-between">
                      <span>Strike Price :</span>
                      <span>{groupInteger(order.strike_price) ?? '-'}</span>
                    </div>
                  </div>
                </div>
                {Number(order.custom_ebtca_product?.buffer_value) > 0 && (
                  <div className="row p-2 border-bottom">
                    <div className="col-4">
                      <div className="d-flex justify-content-between">
                        <span>Buffer % :</span>
                        <span>
                          {(() => {
                            const buf = Number(
                              order.custom_ebtca_product?.buffer_value
                            );
                            const spot = Number(
                              order.custom_ebtca_product?.spot_reference
                            );
                            if (buf > 0 && spot > 0) {
                              return ((buf / spot) * 100).toFixed(6) + ' %';
                            }
                            return '-';
                          })()}
                        </span>
                      </div>
                    </div>
                    <div className="col-4">
                      <div className="d-flex justify-content-between">
                        <span>Buffer Price :</span>
                        <span>
                          {groupInteger(
                            order.custom_ebtca_product?.buffer_value
                          )}
                        </span>
                      </div>
                    </div>
                    <div className="col-4"></div>
                  </div>
                )}
                <div className="row p-2 border-bottom">
                  <div className="col-4">
                    <div className="d-flex justify-content-between">
                      <span>Knock-Out Price :</span>
                      <span>
                        {groupInteger(order.custom_ebtca_product?.ko_price) ??
                          '-'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="row p-2 border-bottom">
                  <div className="col-4">
                    <div className="d-flex justify-content-between">
                      <span>Purchase Amount Ratio :</span>
                      <span>
                        {order.custom_ebtca_product?.boosted_multiplier ?? '-'}
                      </span>
                    </div>
                  </div>
                  <div className="col-4"></div>
                  <div className="col-4">
                    <div className="d-flex justify-content-between">
                      <span>Number of Guarentee Periods :</span>
                      <span>
                        {order.custom_ebtca_product?.guarantee_periods ?? '-'}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between mt-1">
                      <span>Barrier Start Date :</span>
                      <span>{accumBarrierStartDate ?? '-'}</span>
                    </div>
                  </div>
                </div>
                {(() => {
                  const strikeVal = parseNumeric(order.strike_price, 0);
                  const noFixings = parseNumeric(
                    order.custom_ebtca_product?.no_fixings,
                    0
                  );
                  const multiplier = parseNumeric(
                    order.custom_ebtca_product?.boosted_multiplier,
                    0
                  );
                  const orderNotionalAmt = parseNumeric(
                    order.notional_amount,
                    0
                  );
                  const investAmt = parseNumeric(order.investment_amount, 0);
                  const referenceRate = parseNumeric(order.reference_rate, 0);
                  const derivedNotionalAmt = parseFloat(
                    (investAmt * referenceRate).toFixed(2)
                  );
                  const notionalAmt =
                    Number.isFinite(orderNotionalAmt) && orderNotionalAmt > 0
                      ? orderNotionalAmt
                      : derivedNotionalAmt;
                  const spotCcy =
                    order.custom_ebtca_product?.underlying || '';
                  const baseAsset = spotCcy.includes('/')
                    ? spotCcy.split('/')[0]
                    : 'BTC';
                  const notionalCcy = accumNotionalCcy;
                  // Match sheet logic:
                  // Investment Per Fixing = Notional / NoFixings
                  // Purchase Amt 1 = Investment Per Fixing / Multiplier
                  // Purchase Amt 2 = Investment Per Fixing
                  // Amt1/Amt2 (asset qty) = ROUNDDOWN(raw Purchase Amt / Strike, 8)
                  const investmentPerFixing =
                    noFixings > 0 ? notionalAmt / noFixings : 0;
                  const amt1NotionalRaw =
                    multiplier > 0 ? investmentPerFixing / multiplier : 0;
                  const amt2NotionalRaw = investmentPerFixing;
                  // Excel-style truncation to 2dp for purchase amount display.
                  const amt1Notional = floorToDecimals(amt1NotionalRaw, 2);
                  const amt2Notional = floorToDecimals(amt2NotionalRaw, 2);
                  const amt1 =
                    strikeVal > 0
                      ? floorToDecimals(amt1Notional / strikeVal, 8)
                      : 0;
                  const amt2 =
                    strikeVal > 0
                      ? floorToDecimals(amt2Notional / strikeVal, 8)
                      : 0;
                  const fmt = (v) =>
                    new Intl.NumberFormat('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 8,
                    }).format(v || 0);
                  const fmtNotional = (v) =>
                    new Intl.NumberFormat('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(v || 0);
                  return (
                    <div className="row p-2 border-bottom">
                      <div className="col-4">
                        <div className="d-flex justify-content-between">
                          <span>Purchase Amt 1 :</span>
                          <span>
                            {fmtNotional(amt1Notional)} {notionalCcy}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span></span>
                          <small className="text-muted">
                            {fmt(amt1)} {baseAsset}
                          </small>
                        </div>
                      </div>
                      <div className="col-4">
                        <div className="d-flex justify-content-between">
                          <span>Purchase Amt 2 :</span>
                          <span>
                            {fmtNotional(amt2Notional)} {notionalCcy}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span></span>
                          <small className="text-muted">
                            {fmt(amt2)} {baseAsset}
                          </small>
                        </div>
                      </div>
                      <div className="col-4"></div>
                    </div>
                  );
                })()}
              </>
            )}
            {/*<div className="row p-2 border-bottom">
              
             
             {order.term_exercised_amount ? (
                <div className="col-4">
                  <span className="float-left">
                    Return (Reference Rate{" "}
                    {isPut ? String.fromCharCode(60) : String.fromCharCode(62)}{" "}
                    Strike):{" "}
                  </span>
                  <span className="float-right">
                    {showPrice(
                      order.term_exercised_amount,
                      order.term_exercised_amount_ccy
                    )}
                  </span>
                </div>
              ) : (
                <div className="col-4">
                  <span className="float-left">Redemption Amount: </span>
                  <span className="float-right">
                    {order.redeem_amount
                      ? showPrice(order.redeem_amount, order.redeem_amount_ccy)
                      : "TBA"}
                  </span>
                </div>
              )} 
            </div> */}

            {order.option_list && (
              <div>
                <div className="nk-pps-title title mt-2 mb-1 pl-2">
                  Options:
                </div>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Direction</th>
                      <th>From Market Price</th>
                      <th>Strike</th>
                      <th>Notional Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.option_list?.map((option, index) => (
                      <tr key={index}>
                        <td>{option?.optionType}</td>
                        <td>{option?.direction}</td>
                        <td>{parseFloat(option?.mktPctStrike) * 100}%</td>
                        <td>
                          {showPrice(
                            option?.strikePrice,
                            order.invest_ccy,
                            0,
                            true
                          )}
                        </td>
                        <td>
                          {showPrice(
                            order.invest_amount,
                            order.invest_ccy,
                            null,
                            true
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          {/* <div className="mt-3">Rollover</div>
          <div className="nk-odr-list is-stretch card card-bordered ">
            <div className="row p-2 border-bottom">
              <div className="col-4">
                <span className="float-left">Rollover Update Date: </span>
                <span className="float-right">
                  {order.rollover_time
                    ? showDateTime(order.rollover_time)
                    : "NA"}
                </span>
              </div>
              <div className="col-4">
                <span className="float-left">Rollover Amount: </span>
                <span className="float-right">
                  {order.rollover_amt
                    ? showPrice(
                        order.rollover_amt,
                        order.term_expired_amount_ccy
                      )
                    : "0"}
                </span>
              </div>
            </div>
            {isEditMode && (
              <div className="row p-2 border-bottom">
                <div className="col-4 d-flex align-items-baseline">
                  <input
                    type="checkbox"
                    checked={isRolled}
                    onChange={handleIsRolledChange}
                  />
                  <label>&nbsp; Rollover</label>
                </div>
                <div className="col-4 d-flex align-items-baseline justify-content-between">
                  <label style={{ marginRight: "auto" }}>
                    <span>Rollover Amount: </span>
                  </label>
                  <NumericFormat
                    value={rolloverAmt}
                    thousandSeparator={true}
                    onValueChange={handleAmountChange}
                    disabled={!isRolled}
                  />
                  <span className="float-right">
                    &nbsp; {order.term_expired_amount_ccy}
                  </span>
                </div>
                <div className="col-4 d-flex align-items-baseline">
                  <button
                    className="btn btn-light btn-white"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                  &nbsp;&nbsp;&nbsp;
                  <button className="btn btn-primary" onClick={handleSave}>
                    Save
                  </button>
                </div>
              </div>
            )}
          </div> */}
          {isAccumulator && (
            <div>
              <div className="nk-pps-title title mt-2 mb-1">
                ACCUM Fixing and Settlement Schedule
              </div>
              <div className="card card-bordered">
                <table className="table table-ulogs">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Fixing Day</th>
                      <th>Settlement Day</th>
                      <th>{`Purchase Amt 1 ${accumNotionalCcy || ''}`}</th>
                      <th>{`Purchase Amt 2 ${accumNotionalCcy || ''}`}</th>
                      <th>{`Actual Amount ${accumNotionalCcy || ''}`}</th>
                      <th>{`Amount Returned ${accumNotionalCcy || ''}`}</th>
                      {showBtcColumns && <th>BTC Purchased</th>}
                      {showBtcColumns && <th>BTC Fixing Price</th>}
                      {showEthColumns && <th>ETH Purchased</th>}
                      {showEthColumns && <th>ETH Fixing Price</th>}
                      <th>Trade Status</th>
                      {/* <th>KO Price</th> */}
                    </tr>
                  </thead>
                  <tbody>
                    {accumFixingLoading ? (
                      <tr>
                        <td
                          colSpan={accumScheduleColumnCount}
                          className="text-center"
                        >
                          Loading fixing schedule...
                        </td>
                      </tr>
                    ) : accumFixingError ? (
                      <tr>
                        <td
                          colSpan={accumScheduleColumnCount}
                          className="text-center text-danger"
                        >
                          {accumFixingError}
                        </td>
                      </tr>
                    ) : paginatedAccumFixingRows.length > 0 ? (
                      paginatedAccumFixingRows.map((row) => (
                        <tr key={row.id}>
                          <td>{row.id}</td>
                          <td>
                            {showDateTime(row.fixingDate) ||
                              row.fixingDate ||
                              '-'}
                          </td>
                          <td>
                            {showDateTime(row.settlementDate) ||
                              row.settlementDate ||
                              '-'}
                          </td>
                          <td>{formatAccumAmount(accumPurchaseAmount1)}</td>
                          <td>{formatAccumAmount(accumPurchaseAmount2)}</td>
                          <td>{formatActualAmount(row.actualAmount)}</td>
                          <td>{formatActualAmount(row.refundAmount)}</td>
                          {showBtcColumns && (
                            <td>
                              {formatFullPrecisionValue(row.btcPurchased)}
                            </td>
                          )}
                          {showBtcColumns && (
                            <td>{formatFullPrecisionValue(row.btcSpotPrice)}</td>
                          )}
                          {showEthColumns && (
                            <td>
                              {formatFullPrecisionValue(row.ethPurchased)}
                            </td>
                          )}
                          {showEthColumns && (
                            <td>{formatFullPrecisionValue(row.ethSpotPrice)}</td>
                          )}
                          <td>{row.tradeStatus || '-'}</td>
                          {/* <td>{formatScheduleValue(row.koPrice)}</td> */}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={accumScheduleColumnCount}
                          className="text-center"
                        >
                          No fixing schedule available.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {accumFixingRows.length > ACCUM_ITEMS_PER_PAGE && (
                <div className="flex justify-end mt-3 space-x-2">
                  <button
                    onClick={() =>
                      setAccumFixingPage((p) => Math.max(p - 1, 1))
                    }
                    className="btn btn-primary"
                    disabled={accumFixingPage === 1}
                  >
                    Prev
                  </button>
                  <span className="px-2 py-1">
                    {accumFixingPage} / {totalAccumFixingPages}
                  </span>
                  <button
                    onClick={() =>
                      setAccumFixingPage((p) =>
                        Math.min(p + 1, totalAccumFixingPages)
                      )
                    }
                    className="btn btn-primary"
                    disabled={accumFixingPage === totalAccumFixingPages}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
          {order.product.product_type === 'PSFX' ||
          order.product.product_type === 'SSPS' ||
          order.product.product_type === 'PSLFX' ||
          isAccumulator
            ? ''
            : payoutData && (
                <div>
                  <div className="nk-pps-title title mt-2 mb-1">
                    Payout Schedule
                  </div>
                  <div className="card card-bordered">
                    <table className="table table-ulogs">
                      <thead>
                        <tr>
                          <th>Payout Datetime</th>
                          <th>Amount</th>
                          <th>CCY</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedData?.length > 0 ? (
                          paginatedData.map((payout, index) => (
                            <tr key={index}>
                              <td>
                                {payoutDateTime(payout?.payoutDatetime) || '-'}
                              </td>
                              <td>
                                {payout?.amount != null
                                  ? showAmount(payout.amount, payout.ccy)
                                  : '-'}
                              </td>
                              <td>{payout?.ccy || '-'}</td>
                              <td>{payout?.status || '-'}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="4" className="text-center">
                              No records to show
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  {payoutData.length > 5 ? (
                    <div className="flex justify-end mt-4 space-x-2">
                      <button
                        onClick={() =>
                          setCurrentPage((p) => Math.max(p - 1, 1))
                        }
                        className="btn btn-primary"
                        disabled={currentPage === 1}
                      >
                        Prev
                      </button>
                      <span className="px-2 py-1">
                        {currentPage} / {totalPages}
                      </span>
                      <button
                        onClick={() =>
                          setCurrentPage((p) => Math.min(p + 1, totalPages))
                        }
                        className="btn btn-primary"
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </button>
                    </div>
                  ) : (
                    ''
                  )}
                </div>
              )}
          {order.product.product_type === 'SSPS' && (
            <div>
              <div className="nk-pps-title title mt-2 mb-1">
                Daily SSPS Transactions:
              </div>
              <div className="card card-bordered">
                <table className="table table-ulogs">
                  <thead>
                    <tr>
                      <th>Day</th>
                      <th>Date</th>
                      <th>BTC/USDT Reference Price</th>
                      <th>Virtual Purchase Price</th>
                      <th>Daily Return vs Prior Day %</th>
                      <th>{`Daily Participation Amount ${order.invest_ccy}`}</th>
                      <th>Virtual Units</th>
                      <th>{`Balance Notional ${order.invest_ccy}`}</th>
                      <th>Cumulative Virtual Units</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedAccumData?.length > 0 ? (
                      paginatedAccumData.map((accumData, index) => (
                        <tr key={index}>
                          <td>{accumData?.dayNumber || '-'}</td>
                          <td>
                            {showDate(accumData?.calculationDate)
                              ? showDate(accumData?.calculationDate)
                              : '-'}
                          </td>
                          <td>
                            {groupInteger(accumData?.spotReferencePrice) || '-'}
                          </td>
                          <td>
                            {groupInteger(accumData?.adjustedReferencePrice) ||
                              '-'}
                          </td>
                          <td>{accumData?.dailyPerformance || '-'}</td>
                          <td>
                            {groupInteger(accumData?.notionalSpent) || '-'}
                          </td>
                          <td>
                            {groupInteger(accumData?.dailyBoughtBtc) || '-'}
                          </td>
                          <td>
                            {groupInteger(accumData?.balanceNotional) || '-'}
                          </td>
                          <td>
                            {groupInteger(accumData?.totalBtcAccumulated) ||
                              '-'}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-center">
                          No accumulator data available.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {accumDatas && accumDatas.length > ACCUM_ITEMS_PER_PAGE && (
                <div className="flex justify-end mt-3 space-x-2">
                  <button
                    onClick={() =>
                      setAccumCurrentPage((p) => Math.max(p - 1, 1))
                    }
                    className="btn btn-primary"
                    disabled={accumCurrentPage === 1}
                  >
                    Prev
                  </button>
                  <span className="px-2 py-1">
                    {accumCurrentPage} / {totalAccumPages}
                  </span>
                  <button
                    onClick={() =>
                      setAccumCurrentPage((p) =>
                        Math.min(p + 1, totalAccumPages)
                      )
                    }
                    className="btn btn-primary"
                    disabled={accumCurrentPage === totalAccumPages}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
          {(order.commsRate || order.commsFixedRate || order.commsFixedAmt) && (
            <>
              <div className="mt-3">Commission</div>
              <div className="nk-odr-list is-stretch card card-bordered ">
                <div className="row p-2 border-bottom">
                  {order.commsRate ? (
                    <div className="col-4">
                      <span className="float-left">Commission rate: </span>
                      <span className="float-right">{order.commsRate}</span>
                    </div>
                  ) : (
                    ''
                  )}
                  {order.commsFixedRate ? (
                    <div className="col-4">
                      <span className="float-left">
                        Commission fixed rate:{' '}
                      </span>
                      <span className="float-right">
                        {order.commsFixedRate}
                      </span>
                    </div>
                  ) : (
                    ''
                  )}
                  {order.commsFixedAmt ? (
                    <div className="col-4">
                      <span className="float-left">
                        Commission fixed amount:{' '}
                      </span>
                      <span className="float-right">{order.commsFixedAmt}</span>
                    </div>
                  ) : (
                    ''
                  )}
                </div>
              </div>
            </>
          )}
          </>
        ))}

      <Modal
        show={showRateChangeModal}
        onHide={() => setShowRateChangeModal(false)}
      >
        <div className="modal-header">
          <div className="modal-title h4">Agent Rate Change Request</div>
          <button
            type="button"
            className="close"
            aria-label="Close"
            onClick={() => setShowRateChangeModal(false)}
          >
            X
          </button>
        </div>
        <Modal.Body>
          <Form onSubmit={handleAgentRateSubmit}>
            <Row>
              <Col>
                <Form.Group controlId="currentCommissionRate">
                  <Form.Label>Current Commission Rate</Form.Label>
                  <Form.Control
                    type="number"
                    value={currentCommsRate}
                    readOnly
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group controlId="newCommissionRate">
                  <Form.Label>New Commission Rate</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Enter new commission rate"
                    value={newCommissionRate}
                    required
                    onChange={(e) => setNewCommissionRate(e.target.value)}
                    // isInvalid={
                    //   parseFloat(newCommissionRate) >=
                    //   parseFloat(currentCommsRate)
                    // }
                  />
                  <Form.Control.Feedback type="invalid">
                    New commission rate must be smaller than the current rate.
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Row style={{ marginTop: '10px' }}>
              <Col md={6}>
                <Form.Group controlId="fileUpload">
                  <Form.Label>Upload Attachment</Form.Label>
                  <Form.Control
                    type="file"
                    onChange={(e) => setAttachment(e.target.files[0])}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Button
              variant="primary"
              type="submit"
              className="mt-2"
              disabled={loading}
            >
              Submit
              {loading && (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
              )}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
    </Layout>
  );
}
export default Page;
